import AngleRightIcon from 'src/app/assets/icons/ico-angle-right.svg'
import ClockIcon from 'src/app/assets/icons/ico-clock.svg'
import HistoryIcon from 'src/app/assets/icons/ico-history.svg'
// Same glyph the bet panels use for undo/repeat (undo is mirrored via
// the inline-label--undo modifier, matching chip-placement.scss).
import RepeatIcon from 'src/app/assets/icons/ico-redo-alt.svg'
import TrashIcon from 'src/app/assets/icons/ico-trash.svg'
import type { MarbleGame } from 'src/types/domain/Game'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'

type Props = Readonly<{
  /** Whether this is the host panel's open section. */
  open: boolean
  /** The header tap; the host's `activeSectionId` decides what it does. */
  onToggle: () => void
  game: MarbleGame
  /** Exactly the booleans the section's markup reads. */
  flags: Readonly<{
    isCrashGame: boolean
    isChipPlacement: boolean
    /** Birdie: the chip-placement body without its side-bet and auto-bet boxes. */
    isBirdie: boolean
  }>
}>

/**
 * The rules panel's placing-a-bet section — the shared panel's own markup,
 * moved here (5.10c) so that `GameRulesSidebar` and Birdie's rules body mount
 * ONE section instead of two copies. The collapsible chrome (header, chevron,
 * the `open` class) is the panel's; the host keeps the `activeSectionId`
 * state and passes `open` / `onToggle`, so one section is open at a time in
 * either host.
 *
 * Three bodies: crash (two stakes, auto cash-out, auto-bet, timing), chip
 * placement (chips on many options, the side-bet box, auto-bet) and the
 * single-bet games. Birdie is the chip-placement body gated on `isBirdie`:
 * its board takes stakes on many options through the shared chip strip with
 * undo / clear / double / repeat, submits when the betting timer ends and
 * has a bet-history tab — but it sells no side bets and hides the auto-bet
 * toggle, so those two boxes are dropped, and its one box is the last box
 * (no bottom margin, the panel's own convention). Nothing about the other
 * games' markup changes: the flag is false in the shared panel.
 *
 * The section owns its translator on the panel's namespace rather than
 * taking one from the host: the locale extractor files a source file's keys
 * under the namespace named in that file's translation hook call, and these
 * keys already live in the panel's namespace in every locale. The language
 * is read into a name first because the extractor's match cannot see past a
 * nested call in the hook's first argument.
 */
export function PlacingABetSection({ open, onToggle, game, flags }: Props) {
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage, 'gameRules')
  const { isCrashGame, isChipPlacement, isBirdie } = flags
  return (
    <div className={`section section-collapsible ${open ? 'open' : ''}`}>
      <div className="section-header" onClick={onToggle}>
        <h3>{t('Placing a Bet')}</h3>
        <div className="section-header-icon">
          <AngleRightIcon />
        </div>
      </div>
      <div className="section-content">
        {isCrashGame ? (
          <>
            <div className="info-box margin-bottom">
              <h4>{t('Two stakes, one round')}</h4>
              <p>
                {t(
                  'You can place up to two independent stakes each round — run them at different amounts or different auto cash-out targets to play it safe and swing big at the same time.'
                )}
              </p>
              <ul className="info-box-list">
                <li>
                  <span className="inline-label inline-label--wide">{t('Min')}</span>{' '}
                  {t('Set the stake to the minimum')}
                </li>
                <li>
                  <span className="inline-label">×2</span> {t('Double your stake')}
                </li>
                <li>
                  <span className="inline-label">
                    <TrashIcon />
                  </span>{' '}
                  {t('Clear your placed stake')}
                </li>
                <li>
                  <span className="inline-label">
                    <RepeatIcon />
                  </span>{' '}
                  {t('Repeat your last stake')}
                </li>
                <li>
                  <span className="inline-label">
                    <HistoryIcon />
                  </span>{' '}
                  {t('Open Bet History to review your past rounds')}
                </li>
                <li>
                  <span className="inline-label">
                    <ClockIcon />
                  </span>{' '}
                  {t('Stakes lock in when the betting timer ends')}
                </li>
              </ul>
            </div>

            <div className="info-box margin-bottom">
              <h4>{t('Auto Cash-Out')}</h4>
              <p>
                {t(
                  'Set a target multiplier and the game cashes you out automatically the moment the round reaches it — no reflexes required.'
                )}
              </p>
              <ul>
                <li>
                  {t(
                    'Your stake is collected the instant the multiplier hits your target, even if the round crashes a fraction later.'
                  )}
                </li>
                <li>
                  {t(
                    'If the round crashes before your target, that stake is lost like any uncashed play.'
                  )}
                </li>
                <li>
                  {t(
                    'Your auto cash-out target locks in with your bet when the betting timer ends — changes made after that apply from the next round.'
                  )}
                </li>
                <li>
                  {t(
                    'Turning auto cash-out off mid-round keeps it active for the current round (the control turns yellow) and disables it from the next round.'
                  )}
                </li>
                <li>{t('Leave auto cash-out off to cash out by hand whenever you like.')}</li>
              </ul>
            </div>

            <div className="info-box">
              <h4>{t('Auto Betting')}</h4>
              <p>{t('Toggle auto-bet to re-place your stakes automatically every new round.')}</p>
              <ul>
                <li>{t('Auto-bet stops the moment you change a stake or its cash-out target.')}</li>
                <li>
                  {t(
                    'You can turn it off any time by tapping the auto-bet button again, even mid-round.'
                  )}
                </li>
                <li>{t('Auto-bet stops if you run out of balance, disconnect, or refresh.')}</li>
              </ul>
            </div>

            <div className="info-box">
              <h4>{t('Timing, latency & fair play')}</h4>
              <p>
                {t(
                  'Every round is decided on the game server. The live video always runs slightly behind it — a second or two on a good connection, more on a slow one.'
                )}
              </p>
              <ul>
                <li>
                  {t(
                    'A cash-out counts at the moment the server receives it. On a laggy connection the multiplier you saw on screen can differ slightly from the one you are paid at — the paid value is never lower than what your screen showed.'
                  )}
                </li>
                <li>
                  {t(
                    'If the round has already crashed on the server when your cash-out arrives, it is refused — even if your screen had not shown the crash yet. Auto cash-out runs on the server itself, so it is never affected by your connection.'
                  )}
                </li>
                <li>
                  {t(
                    'If you disconnect mid-round, your bet stays live on the server: an auto cash-out target still fires, and results are settled to your balance as normal.'
                  )}
                </li>
                <li>
                  {t(
                    'If the stream fails or a round cannot complete, the round is cancelled and every stake is refunded automatically.'
                  )}
                </li>
              </ul>
            </div>
          </>
        ) : isChipPlacement ? (
          <>
            <div className={isBirdie ? 'info-box' : 'info-box margin-bottom'}>
              <h4>{t('Place chips on as many options as you want')}</h4>
              <p>
                {t(
                  'Each chip you place is its own bet. Stack multiple chips on the same option for a bigger stake, or spread chips across different options to cover more outcomes.'
                )}
              </p>
              <ul className="info-box-list">
                <li>
                  <span className="inline-label inline-label--undo">
                    <RepeatIcon />
                  </span>{' '}
                  {t('Undo your last placed chip')}
                </li>
                <li>
                  <span className="inline-label">
                    <TrashIcon />
                  </span>{' '}
                  {t('Clear all placed chips')}
                </li>
                <li>
                  <span className="inline-label">×2</span> {t('Double every placed stake')}
                </li>
                <li>
                  <span className="inline-label">
                    <RepeatIcon />
                  </span>{' '}
                  {t('Repeat your last round of bets')}
                </li>
                <li>
                  <span className="inline-label">
                    <HistoryIcon />
                  </span>{' '}
                  {t('Open Bet History to review your past bets')}
                </li>
                <li>
                  <span className="inline-label">
                    <ClockIcon />
                  </span>{' '}
                  {t('Bets lock in when the betting timer ends')}
                </li>
              </ul>
            </div>

            {/* Forecast/tricast side-bets only fire on games that
                accept multi-marble selections AND submit ≥ 2 finishing
                positions — otherwise the bet has nowhere to settle.
                Stairpong / Coin Flip are single-result games with
                `maxPickableMarbles = 1`, so the rules block doesn't
                apply to them and would just confuse the player. Birdie
                takes stakes on many options but sells no side bets, so its
                gate is the flag — the config's counts never bring the
                block back. */}
            {!isBirdie && game.maxPickableMarbles > 1 && game.resultsToSubmit > 1 && (
              <div className="info-box margin-bottom">
                <h4>{t('Forecast & Tricast side bets')}</h4>
                <p>
                  {t(
                    'Pick chips on 2 or more main options and you’ll see extra side-bet pills appear below — pick exactly which order the marbles finish in for a much higher payout.'
                  )}
                </p>
                <ul>
                  <li>
                    {t(
                      'Pick 2 marbles → Forecast: backing them to finish 1st and 2nd. Exact Order pays for hitting the order you picked; Any Order pays if both finish in the top 2 in either order.'
                    )}
                  </li>
                  <li>
                    {t(
                      'Pick 3 marbles → Tricast: same idea but for the top 3 podium. Exact Order pays a much higher multiplier; Any Order pays if all 3 land on the podium in any order.'
                    )}
                  </li>
                  <li>
                    {t(
                      'Side-bet pills are independent stakes — you can place chips on as many as you want and they settle separately from your main option bets.'
                    )}
                  </li>
                  <li>
                    {t(
                      'Lightning Rounds and Player Level boosts apply to side bets too — the multiplier you see on each pill already includes any active boosts.'
                    )}
                  </li>
                </ul>
              </div>
            )}
          </>
        ) : (
          <div className="info-box margin-bottom">
            <h4>{t('One bet per race')}</h4>
            <p>{t('Place your bet before the race starts')}</p>
            <ul className="info-box-list">
              <li>
                <span className="inline-label inline-label--wide">{t('Min')}</span>{' '}
                {t('Set the bet to the minimum')}
              </li>
              <li>
                <span className="inline-label">×2</span> {t('Double your bet')}
              </li>
              <li>
                <span className="inline-label">
                  <TrashIcon />
                </span>{' '}
                {t('Clear your placed bet')}
              </li>
              <li>
                <span className="inline-label">
                  <RepeatIcon />
                </span>{' '}
                {t('Repeat your last bet')}
              </li>
              <li>
                <span className="inline-label">
                  <HistoryIcon />
                </span>{' '}
                {t('Open Bet History to review your past bets')}
              </li>
              <li>
                <span className="inline-label">
                  <ClockIcon />
                </span>{' '}
                {t('Only during betting phases')}
              </li>
            </ul>
          </div>
        )}

        {!isCrashGame && !isBirdie && (
          <div className="info-box">
            <h4>{t('Auto Betting')}</h4>
            {isChipPlacement ? (
              <>
                <p>
                  {t(
                    'Toggle auto-bet to keep your placed chips running every new round automatically.'
                  )}
                </p>
                <ul>
                  <li>
                    {t(
                      'Auto-bet stops the moment you change your bet — placing a chip, doubling, undoing, or clearing all turns it off so you never accidentally auto-bet a modified pattern.'
                    )}
                  </li>
                  <li>
                    {t(
                      'You can also turn auto-bet off at any time by tapping the auto-bet button again, even mid-round.'
                    )}
                  </li>
                  <li>{t('Auto-bet stops if you run out of balance, disconnect, or refresh.')}</li>
                </ul>
              </>
            ) : (
              <>
                <p>{t('Repeat same bet automatically until stopped.')}</p>
                <ul>
                  <li>
                    {t(
                      'You can stop auto betting at any time by clicking the auto bet button again or by changing your selection or bet amount.'
                    )}
                  </li>
                  <li>{t('Auto betting stops if disconnected or game is refreshed.')}</li>
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
